import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import clsx from "clsx";

import {
  bulkEditClassesPath,
  bulkEditNextYearClassesPath,
  bulkEditNextYearStudentsPath,
  bulkEditStudentsPath,
  bulkRegisterTeachersPath,
  classesPath,
  copyNotificationPath,
  editClassPath,
  editNextYearClassPath,
  editNextYearStudentPath,
  editNotificationPath,
  editStudentPath,
  editSubClassPath,
  editSubClassStudentPath,
  editTeacherPath,
  exchangeSchoolYearPath,
  gearStatusPath,
  homePath,
  loungeSettingPath,
  newClassPath,
  newNextYearClassPath,
  newNextYearStudentPath,
  newNotificationPath,
  newStudentPath,
  newSubClassPath,
  newTeacherPath,
  nextYearClassesPath,
  nextYearStudentsPath,
  notificationsPath,
  studentsPath,
  subClassesPath,
  teachersPath,
  uploadStudentRealPhotosPath,
  useHistoriesPath,
  subjectsPath,
} from "../../../managerStore";
import useCurrentYear from "../../../hooks/manager/useCurrentYear";
import Modal from "../../../atoms/Modal";

import styles from "./SideMenuBar.module.scss";

const pathsList = [
  [teachersPath, newTeacherPath, editTeacherPath, bulkRegisterTeachersPath],
  [classesPath, newClassPath, editClassPath, bulkEditClassesPath],
  [studentsPath, newStudentPath, editStudentPath, bulkEditStudentsPath],
  [
    exchangeSchoolYearPath,
    nextYearClassesPath,
    newNextYearClassPath,
    editNextYearClassPath,
    bulkEditNextYearClassesPath,
    nextYearStudentsPath,
    newNextYearStudentPath,
    editNextYearStudentPath,
    bulkEditNextYearStudentsPath,
  ],
  [gearStatusPath],
  [subClassesPath, newSubClassPath, editSubClassPath, editSubClassStudentPath],
  [
    notificationsPath,
    newNotificationPath,
    editNotificationPath,
    copyNotificationPath,
  ],
  [useHistoriesPath],
  [loungeSettingPath],
  [subjectsPath],
  [uploadStudentRealPhotosPath],
];

const matchPath = (currentPath: string, paths: string[]) =>
  paths.some(path => currentPath.includes(path));

export const SideMenuBar = (props: {
  roles: number;
  logout: () => void;
  g4s_enable: boolean;
  lounge4t_v2_enabled: boolean;
  change_current_year_status: "open" | "inprogress" | "done";
  is_cram_school: boolean;
}) => {
  const location = useLocation();
  const [showModal, setShowModal] = React.useState(false);
  const history = useHistory();
  const { nextYear } = useCurrentYear();

  const activePath = React.useMemo(() => {
    if (location.pathname === homePath) {
      return homePath;
    }

    // select first path of matched paths
    const matchedPath = pathsList.find(paths =>
      matchPath(location.pathname, paths),
    )?.[0];

    return matchedPath ? matchedPath : "";
  }, [location.pathname]);

  return (
    <>
      <ul className={styles.sideBar}>
        <li
          className={clsx(
            styles.menu,
            activePath === homePath && styles.active,
          )}
          onClick={() => {
            history.push(homePath);
          }}
        >
          はじめに
        </li>
        <li
          className={clsx(
            styles.menu,
            activePath === teachersPath && styles.active,
          )}
          onClick={() => {
            history.push(teachersPath);
          }}
        >
          先生登録
        </li>
        <li
          className={clsx(
            styles.menu,
            activePath === classesPath && styles.active,
          )}
          onClick={() => {
            history.push(classesPath);
          }}
        >
          クラス登録
        </li>
        <li
          className={clsx(
            styles.menu,
            activePath === studentsPath && styles.active,
          )}
          onClick={() => {
            history.push(studentsPath);
          }}
        >
          {props.is_cram_school ? "生徒管理" : "生徒登録"}
        </li>
        {props.is_cram_school && (
          <li
            className={clsx(
              styles.menu,
              activePath === useHistoriesPath && styles.active,
            )}
            onClick={() => {
              history.push(useHistoriesPath);
            }}
          >
            プラン利用状況
          </li>
        )}
        {props.change_current_year_status === "open" && (
          <>
            <li className={styles.groupHeaderItem}>
              <span className={styles.caution}>
                ※{nextYear}年度の利用前に
                <br />
                年度切替が必要です
              </span>
            </li>
            <li
              className={clsx(
                styles.menu,
                !props.lounge4t_v2_enabled && styles.borderBottom,
                activePath === exchangeSchoolYearPath && styles.active,
              )}
              onClick={() => {
                history.push(exchangeSchoolYearPath);
              }}
            >
              年度更新
            </li>
          </>
        )}
        {props.lounge4t_v2_enabled && (
          <>
            <li className={styles.groupHeaderItem}>
              <span className={styles.gearTitle}>LOUNGE</span>
            </li>
            <li
              className={clsx(
                styles.menu,
                activePath === loungeSettingPath && styles.active,
              )}
              onClick={() => {
                history.push(loungeSettingPath);
              }}
            >
              LOUNGE設定
            </li>
            <li
              className={clsx(
                styles.menu,
                activePath === subjectsPath && styles.active,
              )}
              onClick={() => {
                history.push(subjectsPath);
              }}
            >
              教科を登録
            </li>
            <li
              className={clsx(
                styles.menu,
                activePath === uploadStudentRealPhotosPath && styles.active,
                !props.g4s_enable && styles.borderBottom,
              )}
              onClick={() => {
                history.push(uploadStudentRealPhotosPath);
              }}
            >
              生徒顔写真登録
            </li>
          </>
        )}
        {props.g4s_enable && (
          <>
            <li className={styles.groupHeaderItem}>
              <span className={styles.gearTitle}>GEAR</span> for school
            </li>
            <li
              className={clsx(
                styles.menu,
                activePath === gearStatusPath && styles.active,
              )}
              onClick={() => {
                history.push(gearStatusPath);
              }}
            >
              GEAR利用状況・設定
            </li>
            <li
              className={clsx(
                styles.menu,
                activePath === subClassesPath && styles.active,
              )}
              onClick={() => {
                history.push(subClassesPath);
              }}
            >
              GEARグループ登録
            </li>
            <li
              className={clsx(
                styles.menu,
                styles.borderBottom,
                activePath === notificationsPath && styles.active,
              )}
              onClick={() => {
                history.push(notificationsPath);
              }}
            >
              お知らせ追加・編集
            </li>
          </>
        )}
        <li className={styles.menu} onClick={() => setShowModal(true)}>
          ログアウト
        </li>
      </ul>
      {showModal && (
        <Modal onClose={() => setShowModal(false)}>
          <div className={styles.description}>
            ログアウトしてよろしいですか？
          </div>
          <div className={styles.buttonArea}>
            <button
              className={`${styles.btn} ${styles.cancel}`}
              onClick={() => setShowModal(false)}
            >
              キャンセル
            </button>
            <button
              className={`${styles.btn} ${styles.ok}`}
              onClick={() => props.logout()}
            >
              ログアウト
            </button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default SideMenuBar;
